// src/components/Dashboard/DashboardNavBar.js
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaQuestionCircle } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';

const DashboardNavBar = ({
  hasRecordingsAccess = false,
  hasSubscriptionAccess = false,
  hasAdminSubscriptionAccess = false,
}) => {
  const [showSidebar, setShowSidebar] = useState(false);
  const location = useLocation();

  /* Lock body scroll when sidebar is open on mobile */
  useEffect(() => {
    document.body.style.overflow = showSidebar ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showSidebar]);

  const closeSidebar = () => setShowSidebar(false);

  const navLinks = [
    { to: `/dashboard/purchased-videos${location.search}`, label: 'Purchased Videos', always: true },
    { to: `/dashboard/feedback${location.search}`,         label: 'Leave Feedback',    always: true },
    { to: `/dashboard/subscription${location.search}`,     label: 'My Subscription',   show: hasSubscriptionAccess },
    { to: `/dashboard/admin-subscription${location.search}`, label: 'Admin Subscriptions', show: hasAdminSubscriptionAccess },
    { to: `/dashboard/recordings${location.search}`,       label: 'Recordings',        show: hasRecordingsAccess },
  ].filter((l) => l.always || l.show);

  return (
    <>
      <nav className="bg-white border-b border-gray-100 shadow-small sticky top-0 z-[900]">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">

          {/* Brand + tooltip */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-bold text-base bg-gradient-to-r from-accent-teal to-accent-orange bg-clip-text text-transparent select-none">
              My Videos
            </span>
            <Tooltip
              title={
                <div className="p-2 text-sm leading-snug max-w-xs">
                  <div className="font-bold mb-1">My Videos Dashboard</div>
                  <div className="mb-2">
                    Access your purchased videos, manage recordings, leave feedback, and manage your
                    subscription all in one place.
                  </div>
                  <div className="italic text-white/60 text-xs mt-2 pt-2 border-t border-white/20">
                    <div className="mb-1">If you experience download issues, try desktop or another mobile browser.</div>
                    <div>Leave us feedback below or via the "Leave Feedback" tab.</div>
                  </div>
                </div>
              }
              arrow
              placement="bottom-start"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(0,0,0,0.88)',
                    '& .MuiTooltip-arrow': { color: 'rgba(0,0,0,0.88)' },
                    borderRadius: '10px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                  },
                },
              }}
            >
              <button
                className="w-5 h-5 flex items-center justify-center text-text-secondary hover:text-accent-orange transition-colors duration-200 bg-transparent border-none p-0 cursor-pointer"
                aria-label="Dashboard help"
              >
                <FaQuestionCircle className="text-base" />
              </button>
            </Tooltip>
          </div>

          {/* Desktop tab links */}
          <div className="hidden sm:flex items-center gap-1 flex-1 justify-center overflow-x-auto scrollbar-none">
            {navLinks.map(({ to, label }) => (
              <NavLink
                key={label}
                to={to}
                onClick={closeSidebar}
                className={({ isActive }) =>
                  [
                    'whitespace-nowrap px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-150',
                    isActive
                      ? 'bg-brand-red/10 text-brand-red font-semibold'
                      : 'text-text-secondary hover:text-text-primary hover:bg-gray-100',
                  ].join(' ')
                }
              >
                {label}
              </NavLink>
            ))}
          </div>

          {/* Hamburger — mobile only */}
          <button
            onClick={() => setShowSidebar((p) => !p)}
            className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg text-text-primary hover:bg-gray-100 transition-colors duration-150 border-none bg-transparent shrink-0"
            aria-label={showSidebar ? 'Close navigation' : 'Open navigation'}
            aria-expanded={showSidebar}
          >
            {showSidebar ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      {showSidebar && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm sm:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Mobile slide-in drawer */}
      <aside
        className={[
          'fixed top-0 left-0 bottom-0 z-50 w-64 bg-white shadow-2xl sm:hidden',
          'flex flex-col transition-transform duration-300 ease-in-out',
          showSidebar ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        aria-label="Dashboard navigation"
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 shrink-0">
          <span className="font-bold text-base bg-gradient-to-r from-accent-teal to-accent-orange bg-clip-text text-transparent">
            My Videos
          </span>
          <button
            onClick={closeSidebar}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-text-secondary hover:bg-gray-100 transition-colors"
            aria-label="Close navigation"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Drawer links */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {navLinks.map(({ to, label }) => (
            <NavLink
              key={label}
              to={to}
              onClick={closeSidebar}
              className={({ isActive }) =>
                [
                  'flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 border-l-4',
                  isActive
                    ? 'bg-brand-red/10 text-brand-red border-brand-red font-semibold'
                    : 'text-text-secondary hover:text-text-primary hover:bg-gray-50 border-transparent',
                ].join(' ')
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
};

DashboardNavBar.propTypes = {
  hasRecordingsAccess:       PropTypes.bool,
  hasSubscriptionAccess:     PropTypes.bool,
  hasAdminSubscriptionAccess: PropTypes.bool,
};

export default DashboardNavBar;
